import { Save, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import SystemMapAutomationPanel from './SystemMapAutomationPanel';
import {
  deleteMapEdge,
  edgeTypeLabel,
  getEdgeSourceAnchor,
  getEdgeTargetAnchor,
  getEdgeWaypoints,
  kindLabel,
  layerLabel,
  MAP_NODE_KINDS,
  mergeEdgeMetadata,
  updateMapEdge,
  updateMapNodeKind,
  updateNodeAnnotations,
  uploadMapNodeScreenshot,
  type MapEdgeRow,
  type MapNodeKind,
  type MapNodeRow,
} from '../../lib/systemMap';

interface SystemMapDetailPanelProps {
  node: MapNodeRow | null;
  edge: MapEdgeRow | null;
  nodeRows: MapNodeRow[];
  selectedWaypointIndex: number | null;
  onNodeUpdated: (node: MapNodeRow) => void;
  onEdgeUpdated: (edge: MapEdgeRow) => void;
  onEdgeDeleted: (edgeId: string) => void;
  onClose: () => void;
  onMapReload?: () => void;
}

export default function SystemMapDetailPanel({
  node,
  edge,
  nodeRows,
  selectedWaypointIndex,
  onNodeUpdated,
  onEdgeUpdated,
  onEdgeDeleted,
  onClose,
  onMapReload,
}: SystemMapDetailPanelProps) {
  const [notes, setNotes] = useState('');
  const [nodeKind, setNodeKind] = useState<MapNodeKind>('workflow');
  const [edgeLabel, setEdgeLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRemoveEdge, setConfirmRemoveEdge] = useState(false);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setNotes(node?.annotations?.notes ?? '');
    setNodeKind(node?.kind ?? 'workflow');
  }, [node?.id, node?.annotations?.notes, node?.kind]);

  useEffect(() => {
    setEdgeLabel(edge?.label ?? '');
    setConfirmRemoveEdge(false);
  }, [edge?.id, edge?.label]);

  if (!node && !edge) {
    return null;
  }

  const nodeById = (id: string) => nodeRows.find((row) => row.id === id);

  const handleSaveNotes = async () => {
    if (!node) return;
    setSaving(true);
    try {
      const updated = await updateNodeAnnotations(node.id, { notes: notes.trim() || undefined });
      onNodeUpdated(updated);
      toast.success('Notes saved');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdgeLabel = async () => {
    if (!edge) return;
    setSaving(true);
    try {
      const updated = await updateMapEdge(edge.id, { label: edgeLabel.trim() || null });
      onEdgeUpdated(updated);
      toast.success('Connector label saved');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save label');
    } finally {
      setSaving(false);
    }
  };

  const handleResetEdgePath = async () => {
    if (!edge) return;
    setSaving(true);
    try {
      const updated = await updateMapEdge(edge.id, {
        metadata: mergeEdgeMetadata(edge.metadata, { waypoints: [] }),
      });
      onEdgeUpdated(updated);
      toast.success('Connector path reset');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to reset path');
    } finally {
      setSaving(false);
    }
  };

  const handleResetEdgeAnchors = async () => {
    if (!edge) return;
    setSaving(true);
    try {
      const updated = await updateMapEdge(edge.id, {
        metadata: mergeEdgeMetadata(edge.metadata, { sourceAnchor: undefined, targetAnchor: undefined }),
      });
      onEdgeUpdated(updated);
      toast.success('Connection points reset');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to reset connection points');
    } finally {
      setSaving(false);
    }
  };

  const handleKindChange = async (nextKind: MapNodeKind) => {
    if (!node || nextKind === node.kind) return;
    setNodeKind(nextKind);
    setSaving(true);
    try {
      const updated = await updateMapNodeKind(node.id, nextKind);
      onNodeUpdated(updated);
      toast.success(`Type set to ${kindLabel(nextKind)}`);
    } catch (e: unknown) {
      setNodeKind(node.kind);
      toast.error(e instanceof Error ? e.message : 'Failed to update node type');
    } finally {
      setSaving(false);
    }
  };

  const handleScreenshotUpload = async (file: File) => {
    if (!node) return;
    setSaving(true);
    try {
      const updated = await uploadMapNodeScreenshot(node.id, node.slug, file);
      onNodeUpdated(updated);
      toast.success('Screenshot uploaded');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to upload screenshot');
    } finally {
      setSaving(false);
      if (screenshotInputRef.current) screenshotInputRef.current.value = '';
    }
  };

  const handleRemoveEdge = async () => {
    if (!edge) return;
    setSaving(true);
    try {
      await deleteMapEdge(edge.id);
      onEdgeDeleted(edge.id);
      toast.success('Connector removed');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to remove connector');
    } finally {
      setSaving(false);
      setConfirmRemoveEdge(false);
    }
  };

  if (edge) {
    const source = nodeById(edge.source_node_id);
    const target = nodeById(edge.target_node_id);
    const bendCount = getEdgeWaypoints(edge.metadata).length;
    const sourceAnchor = getEdgeSourceAnchor(edge.metadata);
    const targetAnchor = getEdgeTargetAnchor(edge.metadata);
    const customAnchors =
      edge.metadata.sourceAnchor != null || edge.metadata.targetAnchor != null;

    return (
      <aside className="w-80 shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col max-h-full overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Connector</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {edgeTypeLabel(edge.edge_type)}
              {bendCount > 0 ? ` · ${bendCount} bend${bendCount === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              From → To
            </h3>
            <p className="text-gray-700 dark:text-gray-300">
              {source?.label ?? 'Unknown'} → {target?.label ?? 'Unknown'}
            </p>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Label
            </h3>
            <input
              type="text"
              value={edgeLabel}
              onChange={(e) => setEdgeLabel(e.target.value)}
              placeholder="Connector label…"
              className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <button
              type="button"
              onClick={() => void handleSaveEdgeLabel()}
              disabled={saving}
              className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              Save label
            </button>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <p className="font-medium text-gray-700 dark:text-gray-300">Shape the line</p>
            <p>Routes automatically use the shortest orthogonal path.</p>
            <p>Drag the blue dots at each end to change where the line attaches on a node.</p>
            <p>
              Select the connector, then drag a segment handle — up/down on horizontal runs,
              left/right on vertical runs — to route around other nodes.
            </p>
            <p>Delete resets the route to the auto shortest path.</p>
          </div>

          {customAnchors ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Attachment
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                From: {sourceAnchor.side} ({Math.round(sourceAnchor.offset * 100)}%) · To:{' '}
                {targetAnchor.side} ({Math.round(targetAnchor.offset * 100)}%)
              </p>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {bendCount > 0 ? (
              <button
                type="button"
                onClick={() => void handleResetEdgePath()}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Reset route
              </button>
            ) : null}
            {customAnchors ? (
              <button
                type="button"
                onClick={() => void handleResetEdgeAnchors()}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Reset attachment
              </button>
            ) : null}
          </div>

          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            {confirmRemoveEdge ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Remove this connector permanently? Nodes stay on the map.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void handleRemoveEdge()}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Confirm remove
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmRemoveEdge(false)}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmRemoveEdge(true)}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove connector
              </button>
            )}
          </div>
        </div>
      </aside>
    );
  }

  if (!node) return null;

  return (
    <aside className="w-80 shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col max-h-full overflow-hidden">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">{node.label}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {kindLabel(node.kind)} · {layerLabel(node.layer)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Node type
          </h3>
          <select
            value={nodeKind}
            onChange={(e) => void handleKindChange(e.target.value as MapNodeKind)}
            disabled={saving}
            className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
          >
            {MAP_NODE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kindLabel(kind)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Controls map color and the type filter pill. Layer stays {layerLabel(node.layer)}.
          </p>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Screenshot
          </h3>
          {node.kind !== 'automation' ? (
            <>
              {node.show_thumbnail && node.screenshot_url ? (
                <img
                  src={node.screenshot_url}
                  alt={`Screenshot of ${node.label}`}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 mb-2"
                />
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">No screenshot yet.</p>
              )}
              {node.captured_at ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Captured {new Date(node.captured_at).toLocaleString()}
                </p>
              ) : null}
              <input
                ref={screenshotInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleScreenshotUpload(file);
                }}
              />
              <button
                type="button"
                onClick={() => screenshotInputRef.current?.click()}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" />
                {node.screenshot_url ? 'Replace screenshot' : 'Upload screenshot'}
              </button>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">PNG, JPEG, or WebP · max 5 MB</p>
            </>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Automation nodes use per-step screenshots in the workflow section below.
            </p>
          )}
        </div>

        {node.role_description ? (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Role
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{node.role_description}</p>
          </div>
        ) : null}

        {node.usage_notes ? (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Usage
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{node.usage_notes}</p>
          </div>
        ) : null}

        {node.url ? (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              URL
            </h3>
            <a
              href={node.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 hover:underline break-all"
            >
              {node.url}
            </a>
          </div>
        ) : null}

        {node.repo_path ? (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Repo path
            </h3>
            <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded break-all">{node.repo_path}</code>
          </div>
        ) : null}

        {node.kind === 'automation' ? (
          <SystemMapAutomationPanel
            node={node}
            saving={saving}
            setSaving={setSaving}
            onNodeUpdated={onNodeUpdated}
            onMapReload={onMapReload}
          />
        ) : null}

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Your notes
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Annotations for this node…"
            className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            type="button"
            onClick={() => void handleSaveNotes()}
            disabled={saving}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            Save notes
          </button>
        </div>
      </div>
    </aside>
  );
}
